import React, { useState, useMemo, useEffect } from "react";
import { Button, SwipeableDrawer, styled, Box, Divider, Avatar, Drawer, IconButton } from "@mui/material";
import { formatNumberInString, getAvatarName, nFormatter } from "../../../utils";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "../../../base/useMediaQuery";
import clsx from "clsx";
import { Close, FmdGood, SmsFailed, Tour } from "@mui/icons-material";
import { applyForJob, getJobById, markJobShortList, removeJobShortList, withdrawJobApplication } from "../../actions/auditions";
import { useMessage } from "../../../base/MessageProvider";
import { getDirectorById, markRemoveDirectorFavorite } from "../../actions/user";
import { useNavigate } from "react-router-dom";
import { FormTypes } from "../../pages/Profile/TabPanels/StatsTab";
import { setKycModal, setReportModal } from "../../actions/general";
import OAuth from "../OAuth";

const CustomSwipeableDrawer = styled(SwipeableDrawer)`
    & .MuiDrawer-paperAnchorBottom {
        border-top-left-radius: 22px !important;
        border-top-right-radius: 22px !important;
        height: 94%;
        background-color: #3A3A3A;
    }
`;

const CustomDrawer = styled(Drawer)`
    & .MuiDrawer-paperAnchorRight {
        background-color: #F6F6F6;
        max-width: 710px;
        width: 100%;
    }
`;


const AuditionView = ({ open, onClose }) => {
    const data = useSelector((state) => state.artistAuditionReducer?.currentJob);
    const metaData = useSelector((state) => state.artistAuditionReducer?.currentJobMeta);

    const userData = data?.user;

    const min620px = useMediaQuery('(min-width: 620px)');

    const alert = useMessage();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fullName = `${data?.user?.firstName ?? ""} ${data?.user?.lastName ?? ""}`;
    const profilePic = data?.user?.userProfile?.profilePic?.url;

    if (!data) return;

    const buttonTitle = data?.applied ? "Applied" : new Date(data.endDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) ? "Expired" : "Apply Now";
    const buttonDisabled = data?.applied || new Date(data.endDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
    const bgColor = buttonDisabled ? "lightgrey" : (data?.applied ? "grey" : "#0D99FF");

    const errorCallback = (error) => {
        alert.showError(error.message);
    }

    const successCallback = (res) => {
        alert.showSuccess(res.message);
        dispatch(getJobById(data.jobId))
    }

    const badRequestCallback = (response) => {
        alert.showInfo(response?.message);
        const { profileCompleted, kycVerified } = response?.metadata || {};
        if (profileCompleted === false || kycVerified === false) {
            dispatch(setKycModal({
                open: true, profileCompleted, kycVerified
            }));
        }
    }

    const handleShortlistJob = async (e) => {
        if (data?.shortlisted) {
            dispatch(removeJobShortList(data.jobId, successCallback, errorCallback))
        } else {
            dispatch(markJobShortList(data.jobId, successCallback, errorCallback))
        }
    }
    
    const handleApplyJob = async () => {
        if (data?.applied) {
            dispatch(withdrawJobApplication(data.jobId, successCallback, errorCallback))
        } else {
            dispatch(applyForJob(data.jobId, badRequestCallback, successCallback, errorCallback))
        }
    }

    const onClickApplyJob = (e) => {
        if (data?.applied) {
            alert.showConfirmation2({
                message: `Are you sure you to withdraw job?`,
                button1Title: 'Cancel',
                button2Title: 'Confirm',
                onPressBtn1: () => {},
                onPressBtn2: handleApplyJob,
            });
        } else handleApplyJob();
    }

    const handleToggleFollow = () => {
        dispatch(markRemoveDirectorFavorite(userData?.userId, !metaData?.is_favourite, (res) => {
            alert.showSuccess(`${metaData?.is_favourite ? 'Unfollowed' : 'Following'} ${userData?.firstName}`);
            dispatch(getJobById(data.jobId))
        }, errorCallback))
    }

    const handleClickProfile = (onJobs = false) => {
        dispatch(getDirectorById({ profileId: userData?.userId }, () => {
            navigate(`/artist/recruiter-profile?profileId=${userData?.userId}`, {
                state: onJobs ? { openTab: 1 } : undefined
            });
        }, (error) => {
            alert.showError(error?.message ?? "Something went wrong!!");
        }));
    }

    const handleCompleteProfile = () => {
        navigate(`/artist/profile/${FormTypes.STATS_INFO}`);
    }

    const handleReportJob = () => {
        dispatch(setReportModal({
            open: true, data, type: "job"
        }));
    }

    if (min620px) {
        return (
            <CustomDrawer anchor="right" open={open} onClose={onClose}>
                <Box className="bg-[#F6F6F6] w-full px-5 max-w-[720px] h-full flex flex-col justify-center overflow-hidden">
                    <IconButton onClick={onClose} size="small" className="absolute top-1 left-1">
                        <Close />
                    </IconButton>
                    <Box className="bg-white border-[2px] border-[#050303] rounded-l-[20px] h-[90vh] scroll">
                        <div className="p-5">
                            <div className="flex items-center justify-between w-full mb-4">
                                <div className="text-[#282828] text-[12px] uppercase font-['Poppins-Regular']">{data?.projectCategory?.name}</div>
                                <div className="flex gap-4 items-center">
                                    <div className="bg-[#DEDEDE] rounded-full text-black text-[12px] text-center px-4 py-1 font-['Poppins-Regular']">{data?.profileCategory?.name}</div>
                                    <div className="bg-[#DEDEDE] rounded-full text-black text-[12px] text-center px-4 py-1 font-['Poppins-Regular']">{data?.roleType?.name}</div>
                                </div>
                            </div>
                            <h1 className="text-[#282828] text-[16px] font-['Poppins-Medium'] mb-4">{`${data?.roleType?.name} casting for ${data?.projectCategory?.name}`}</h1>
                            <div className="flex items-center justify-between w-full">
                                <div className="flex gap-4 items-center">
                                    <h2 className="text-[#656565] text-[11px] text-center font-['Poppins-Medium']">Age: {data?.minAge}-{data?.maxAge}</h2>
                                    <h2 className="text-[#656565] text-[11px] text-center font-['Poppins-Medium']">{data?.gender}</h2>
                                </div>
                                {data?.auditionLocation ? (
                                    <div className="flex gap-1 items-center">
                                        <FmdGood className="text-[16px] text-[#2F80ED]" />
                                        <h2 className="text-[#656565] text-[11px] font-['Poppins-Medium']">{data?.auditionLocation}</h2>
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex items-center justify-between w-full mb-4">
                                <h2 className="text-[#A1AA9C] text-[10px] text-center font-['Poppins-Regular']">Posted on {format(new Date(data.addedAt), 'dd MMM yyyy')}</h2>
                                <h2 className="text-[#A1AA9C] text-[10px] text-center font-['Poppins-Regular']">Expires: {format(new Date(data.endDate), 'dd MMMM yyyy')}</h2>
                            </div>
                            <div className="flex gap-1 mb-4">
                                <SmsFailed className="text-[#455B45] text-lg mt-[2px]" />
                                <h1 className="text-[#455B45] text-[12px] font-['Poppins-Regular'] ">
                                    Completed profiles can help you better highlight your expertise when applying for auditions like these. <a onClick={handleCompleteProfile} className="text-[#2F80ED] hover:underline active:text-[#2860aa] cursor-pointer">Complete your Profile</a>
                                </h1>
                            </div>
                            <div className="flex gap-1 items-center">
                                <Tour className="text-[#EF4747] text-lg" />
                                <h1 onClick={handleReportJob} className="text-[#EF4747] text-[12px] font-['Poppins-Regular'] hover:underline cursor-pointer active:text-gray-600">
                                    Flag as inappropriate 
                                </h1>
                            </div>
                        </div>
                        <Divider className="w-full border-[#939393] border-b-[2px]"/>
                        <div className="px-5 flex flex-col items-center gap-2">
                            <div className="bg-[#939393] w-full max-w-[175px] text-white text-[12px] text-center ml-5 rounded-b-[11px] px-4 py-2 font-['Poppins-Medium']">Project Description</div>
                            <p className="text-[14px] text-[#505050] font-['CG-Regular'] my-4">{data?.project?.description}</p>
                        </div>
                        <Divider className="w-full border-[#939393] border-b-[2px]"/>
                        <div className="flex flex-col items-center">
                            <div className="bg-[rgb(147,147,147)] w-full max-w-[175px] text-white text-[12px] text-center ml-5 rounded-b-[11px] px-4 py-2 font-['Poppins-Medium']">Project Details</div>
                            <div className="flex w-full">
                                <div className="flex flex-col w-[50%] p-5 border-[#939393] border-r-[2px]">
                                    {data?.project?.title ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2">
                                            <span className="font-['CG-Medium'] font-[500]">Project -</span> {data?.project?.title}
                                        </p>
                                    ) : null}
                                    {data?.language ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2">
                                            <span className="font-['CG-Medium'] font-[500]">Language -</span> {data?.language}
                                        </p>
                                    ) : null}
                                    {data?.numberOfDays ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2">
                                            <span className="font-['CG-Medium'] font-[500]">Duration -</span> {data?.numberOfDays}
                                        </p>
                                    ) : null}
                                    {data?.shootLocation ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2">
                                            <span className="font-['CG-Medium'] font-[500]">Shoot Location -</span> {data?.shootLocation}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="flex flex-col w-[50%] p-5">
                                    {!data.negotiable && (data?.minBudget || data?.maxBudget) ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2 w-full">
                                            <span className="font-['CG-Medium'] font-[500]">Budget -</span> {formatNumberInString(+data?.minBudget || 0)} - {formatNumberInString(+data?.maxBudget || 0)}
                                        </p>
                                    ) : null}
                                    {data.negotiable ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2 w-full">
                                            <span className="font-['CG-Medium'] font-[500]">Budget -</span> Depends on artist
                                        </p>
                                    ) : null}
                                    {data?.payBasis ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2">
                                            <span className="font-['CG-Medium'] font-[500]">Pay Basis -</span> {data?.payBasis}
                                        </p>
                                    ) : null}
                                    {data?.characterDescription ? (
                                        <p className="text-[14px] font-['CG-Regular'] my-2">
                                            <span className="font-['CG-Medium'] font-[500]">Requirements -</span> {data?.characterDescription}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <Divider className="w-full border-[#939393] border-b-[2px]"/>
                        <div className="p-5 flex justify-between items-center">
                            <div className="flex gap-3 items-center">
                                <Button onClick={handleClickProfile?.bind(null)} className="flex gap-2 p-1 text-[#000] normal-case rounded-xl cursor-pointer select-none">
                                    <OAuth component={
                                        <Avatar src={profilePic} className="w-[35px] h-[35px] text-[14px]">{getAvatarName(fullName)}</Avatar>
                                    } />
                                    <div className="flex flex-col justify-between">
                                        <h2 className="font-['Poppins-Medium'] font-[500] text-[13px] text-left">{fullName}</h2>
                                        <span className="text-[11px] font-['Poppins-Regular'] text-left">{data?.user?.userProfile?.profileCategory?.name}</span>
                                    </div>
                                </Button>
                                <Button onClick={handleToggleFollow} variant={metaData?.is_favourite && 'outlined'} className={clsx("rounded-full text-[12px] normal-case ", {
                                    "bg-[#666666] text-white": !metaData?.is_favourite, "border-[#666666] border-[2px]": metaData?.is_favourite
                                })}>{metaData?.is_favourite ? 'Following' : 'Follow'}</Button>
                            </div>
                            <ul>
                                {metaData?.jobs_posted !== null && +metaData?.jobs_posted > 0 && (
                                    <li className="text-[10px] font-['Poppins-Regular']">&#x2022; {nFormatter(metaData.jobs_posted ?? 0)} Job Posted</li>
                                )}
                                {metaData?.application_receive !== null && +metaData?.application_receive > 0 && (
                                    <li className="text-[10px] font-['Poppins-Regular']">&#x2022; {nFormatter(metaData.application_receive ?? 0)} Application Received</li>
                                )}
                                {metaData?.application_receive !== null && +metaData?.application_receive > 0 && (
                                    <li onClick={handleClickProfile?.bind(null, true)} className="text-[10px] text-green-500 cursor-pointer hover:underline font-['Poppins-Regular']">&#x2022; {nFormatter(metaData.application_receive ?? 0)} Active Jobs</li>
                                )}
                            </ul>
                        </div>
                        <Divider className="w-full border-[#939393] border-b-[2px]"/>
                        <div className="p-5 flex justify-center gap-7 items-center">
                            <Button onClick={handleShortlistJob} variant="outlined" className={clsx("px-3 text-[14px] font-['Poppins-Medium'] min-w-[80px] normal-case rounded-full", {
                                "border-gray-700 text-gray-700": data?.shortlisted, "border-[#2F80ED] text-[#2F80ED]": !data?.shortlisted
                            })}>
                                {data?.shortlisted ? 'Shortlisted' : 'Shortlist'}
                            </Button>
                            <Button onClick={onClickApplyJob}
                                disabled={buttonDisabled} style={{ backgroundColor: bgColor }}
                                className={clsx("text-white text-[14px] font-['Poppins-Medium'] normal-case rounded-full min-w-[80px] px-3")}
                            >{buttonTitle}</Button>
                        </div>
                    </Box>
                </Box>
            </CustomDrawer>
        )
    }

    return (
        <CustomSwipeableDrawer anchor="bottom" open={open} onClose={onClose}>
            <Box className="py-4 px-5" sx={{ width: 'auto' }}>
                <div className="w-[54px] h-1 bg-[#D9D9D9] rounded-sm mx-auto" />
                <div className="py-5 text-white overflow-hidden">
                    <div className="flex justify-between items-center my-2">
                        <div className="text-white bg-[#00000065] rounded-[3px] text-[12px] text-center px-5 py-[4px] font-['Poppins-Medium']">{data?.projectCategory?.name}</div>
                        <div className="flex gap-1 items-center">
                            <div className="text-white bg-[#00000065] rounded-[3px] text-[12px] text-center px-5 py-[4px] font-['Poppins-Medium']">{data?.profileCategory?.name}</div>
                            <div className="text-white bg-[#00000065] rounded-[3px] text-[12px] text-center px-5 py-[4px] font-['Poppins-Medium']">{data?.roleType?.name}</div>
                        </div>
                    </div>
                    <h1 className="text-white rounded-[6px] my-5 text-[24px] font-['CG-Medium']">{data?.title}</h1>
                    <div className="flex items-center justify-between my-2">
                        <p className="text-[12px] font-['CG-Regular'] font-[400]">
                            {data?.gender}, {"  "} Age: {data?.minAge}-{data?.maxAge}
                        </p>
                        <p className="text-[12px] font-['CG-Regular'] font-[400]">
                            {data?.auditionLocation}
                        </p>
                    </div>
                    <Divider className="my-2" />
                    <div className="scroll h-[52vh]">
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Project -</span> {data?.project?.title}
                        </p>
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Project Description -</span> {data?.project?.description}
                        </p>
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Language -</span> {data?.language}
                        </p>
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Duration -</span> {data?.numberOfDays}
                        </p>
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Shoot Location -</span> {data?.shootLocation}
                        </p>
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Budget -</span> {formatNumberInString(+data?.minBudget || 0)} - {formatNumberInString(+data?.maxBudget || 0)}
                        </p>
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Pay Basis -</span> {data?.payBasis}
                        </p>
                        <p className="text-[16px] font-['CG-Regular'] my-2">
                            <span className="font-['CG-Medium'] font-[500]">Requirements -</span> {data?.characterDescription}
                        </p>
                    </div>
                    <Divider className="my-2" />
                    <div className="flex justify-between gap-1">
                        <h2 className="font-['Poppins-Regular'] font-[400] text-[10px] text-[#FFFFFF]">Posted: {format(new Date(data?.addedAt), "dd MMMM yyyy")}</h2>
                        <h2 className="font-['Poppins-Regular'] font-[400] text-[10px] text-[#ffffff]">Expires: {format(new Date(data?.endDate), "dd MMMM yyyy")}</h2>
                    </div>
                    <Divider className="my-2" />
                    <div className="flex gap-2 my-4">
                        <Avatar src={profilePic} className="w-[30px] h-[30px] text-[14px]">{getAvatarName(fullName)}</Avatar>
                        <div className="flex flex-col justify-between">
                            <h2 className="font-['Poppins-Medium'] font-[500] text-[11px]">{fullName}</h2>
                            <span className="text-[10px] font-['Poppins-Regular']">{data?.user?.userProfile?.profileCategory?.name}</span>
                        </div>
                    </div>
                </div>
            </Box>
        </CustomSwipeableDrawer>
    );
}

export default React.memo(AuditionView, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open;
});